import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { SUPPORTED_LANGUAGES } from '../../i18n';

/**
 * LanguageSwitcher
 * ----------------------------------------------------------------------------
 * Compact EN/DE toggle in the header. Persists choice via i18next-browser-
 * languagedetector (localStorage key: 'clearequal.lang').
 */
const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    || SUPPORTED_LANGUAGES.find((l) => i18n.language.startsWith(l.code))
    || SUPPORTED_LANGUAGES[0];

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-border bg-card hover:bg-muted transition-smooth"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('language.switcherTitle', 'Language')}
      >
        <span aria-hidden className="text-sm leading-none">{current.flag}</span>
        <span className="hidden 2xl:inline text-xs font-semibold text-foreground uppercase tracking-wide">
          {current.code}
        </span>
        <Icon name="ChevronDown" size={12} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevated z-50 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-border bg-muted/50">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              {t('language.switcherTitle', 'Language')}
            </span>
          </div>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-smooth ${
                  active ? 'bg-primary/5' : 'hover:bg-muted'
                }`}
              >
                <span aria-hidden className="text-base leading-none">{lang.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{lang.nativeLabel}</div>
                  <div className="text-[11px] text-muted-foreground">{lang.label}</div>
                </div>
                {active && <Icon name="Check" size={14} className="text-primary-solid shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
